import { PROJECTS, GALLERY } from "../../data/content";
import { Lock } from "lucide-react";
import { Reveal } from "../Reveal";
import BeforeAfter from "../BeforeAfter";

export default function Projects() {
  return (
    <section id="projects" className="relative bg-navy-900 py-24 sm:py-32 md:py-40">
      <div className="mx-auto max-w-[1440px] px-6 sm:px-12">
        <Reveal>
          <p className="mb-4 text-[11px] uppercase tracking-[0.4em] text-gold">Selected work</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="max-w-3xl font-heading text-4xl font-light leading-tight text-offwhite sm:text-5xl">
            From bare shell to delivered space. <span className="italic text-gold">Drag to reveal.</span>
          </h2>
        </Reveal>

        <div className="mt-20 space-y-28 md:space-y-40">
          {PROJECTS.map((p, i) => (
            <div
              key={p.id}
              data-testid={`project-${p.id}`}
              className={
                "grid items-center gap-10 lg:grid-cols-2 lg:gap-20 " +
                (i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : "")
              }
            >
              <Reveal>
                {p.noPhotos ? (
                  <div
                    data-testid={`no-photos-${p.id}`}
                    className="relative flex aspect-[4/3] w-full flex-col items-center justify-center overflow-hidden rounded-sm border border-gold/20 bg-navy-800 p-10 text-center"
                  >
                    <svg className="absolute inset-0 h-full w-full opacity-[0.12]" viewBox="0 0 400 300" preserveAspectRatio="none" fill="none">
                      <path d="M0 80 H400 M0 220 H400 M120 0 V300 M280 0 V300" stroke="#D4AF37" strokeWidth="1" />
                    </svg>
                    <Lock className="relative mb-6 text-gold" size={28} strokeWidth={1.3} />
                    <p className="relative max-w-sm font-heading text-2xl font-light italic leading-snug text-offwhite">
                      {p.privacyNote}
                    </p>
                  </div>
                ) : (
                  <BeforeAfter before={p.before} after={p.after} testid={`slider-${p.id}`} />
                )}
              </Reveal>

              <Reveal delay={0.1}>
                <div>
                  <div className="flex items-center gap-4">
                    <span className="font-heading text-5xl font-light text-gold/30">{p.index}</span>
                    <span className="text-[11px] uppercase tracking-[0.25em] text-gold">{p.tag}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-3xl font-light text-offwhite sm:text-4xl">{p.name}</h3>
                  <p className="mt-2 text-sm text-offwhite/50">{p.location}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-gold/80">{p.stat}</p>

                  <dl className="mt-8 space-y-4 border-t border-gold/10 pt-8">
                    {[
                      ["Brief", p.brief],
                      ["Challenge", p.challenge],
                      ["Approach", p.approach],
                      ["Result", p.result],
                    ].map(([k, v]) => (
                      <div key={k} className="grid grid-cols-[80px_1fr] gap-4">
                        <dt className="text-[10px] uppercase tracking-[0.2em] text-offwhite/40">{k}</dt>
                        <dd className="text-sm font-light leading-relaxed text-offwhite/75">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </Reveal>
            </div>
          ))}
        </div>

        {/* Texture gallery strip */}
        <Reveal>
          <p className="mt-28 mb-8 text-[11px] uppercase tracking-[0.4em] text-offwhite/50">Details & finishes</p>
        </Reveal>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
          {GALLERY.map((src, i) => (
            <Reveal key={i} delay={i * 0.03}>
              <div data-cursor="hover" className="group relative aspect-square overflow-hidden rounded-sm">
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover grayscale transition-all duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-navy-900/40 transition-opacity duration-500 group-hover:opacity-0" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
